// The event↔product tag mirror — a read-only local copy of every EventProduct
// pair, so the catalogue's "filter the browse list by event" works in a dead
// zone. Full-replaced on every pull, like the leads mirror: a few hundred rows
// of two cuids is not worth an incremental cursor, and a wholesale replace is
// self-healing.
//
// Tagging itself stays online-only (pre-show laptop prep); this mirror is only
// the READ side that lets a booth phone narrow the offline catalogue to the
// products taken to the current show.
import type { AllEventTags } from '@paradise/shared';

import { apiRequest } from '@/lib/api/http';
import { db, type EventTagRow } from '@/lib/db';

export type EventTagsRefreshResult = 'updated' | 'offline' | 'unauthenticated' | 'error';

// Monotonic tokens guarding the full-replace, exactly as `leads-mirror.ts` does:
// several triggers can call this concurrently and a stale late response must not
// clobber a fresher mirror.
let refreshSeq = 0;
let appliedSeq = 0;

function toRow(pair: { eventId: string; productId: string }): EventTagRow {
  return { id: `${pair.eventId}:${pair.productId}`, eventId: pair.eventId, productId: pair.productId };
}

/** Fetch every event↔product pair and FULL-REPLACE the mirror. */
export async function refreshEventTagsMirror(): Promise<EventTagsRefreshResult> {
  const seq = ++refreshSeq;
  const result = await apiRequest<AllEventTags>('/api/event-tags');
  switch (result.kind) {
    case 'ok':
      if (seq < appliedSeq) return 'updated';
      appliedSeq = seq;
      await db.transaction('rw', db.eventTags, async () => {
        await db.eventTags.clear();
        await db.eventTags.bulkPut(result.data.tags.map(toRow));
      });
      return 'updated';
    case 'offline':
      return 'offline';
    case 'unauthenticated':
      // Not our decision — leave the mirror in place so browse-by-event keeps
      // working offline off the last-known tags.
      return 'unauthenticated';
    case 'error':
      return 'error';
  }
}

/** The product ids tagged to one event, read from the offline mirror. */
export async function readEventTaggedIds(eventId: string): Promise<ReadonlySet<string>> {
  const rows = await db.eventTags.where('eventId').equals(eventId).toArray();
  return new Set(rows.map(row => row.productId));
}
